import { useEffect, useState } from "react";
import type { ReactNode } from "react";

import { useTranslation } from "react-i18next";

import * as Dialog from "@radix-ui/react-dialog";

import styles from "./NoteWorkflows.module.css";
import { NodeTaskSettings } from "./NodeTaskSettings";
import { editableBoxPermissions, serializeBoxPermissions } from "./boxTaskTypes";
import type { BoxPermissionSettings, BoxTaskSettings } from "./boxTaskTypes";
import type { OrganizationMemberDirectory } from "../../pages/organization-admin/types";

import { WorkflowNodeSelection } from "./noteWorkflowsTypes";

import { MAX_DESCRIPTION_LENGTH, MAX_TITLE_LENGTH, MAX_TRIGGER_DESCRIPTION_LENGTH } from "./noteWorkflowsSchema";

interface WorkflowNodeDialogProps {
    open: boolean;

    selection: WorkflowNodeSelection | null;

    title: string;

    description: string;

    onOpenChange: (open: boolean) => void;

    taskSettings?: BoxTaskSettings;
    directory: OrganizationMemberDirectory | null;
    membersLoading: boolean;
    membersError: string | null;
    onRetryMembers: () => void;

    onSave: (title: string, description: string, taskSettings?: BoxTaskSettings) => void;

    onDelete: () => void;
    children?: ReactNode;
}

export function WorkflowNodeDialog({
    open,

    selection,

    title,

    description,

    onOpenChange,

    onSave,

    taskSettings,
    directory,
    membersLoading,
    membersError,
    onRetryMembers,

    onDelete,
    children
}: WorkflowNodeDialogProps) {
    const { t } = useTranslation();

    const [titleDraft, setTitleDraft] = useState(title);

    const [descriptionDraft, setDescriptionDraft] = useState(description);

    const [taskDraft, setTaskDraft] = useState<BoxPermissionSettings>(() => editableBoxPermissions(taskSettings));
    const isPurpleNode = selection !== null && selection.nodeKind !== "trigger";

    const maxDescriptionLength = selection?.nodeKind === "trigger" ? MAX_TRIGGER_DESCRIPTION_LENGTH : MAX_DESCRIPTION_LENGTH;

    useEffect(() => {
        if (open) {
            setTitleDraft(title);

            setDescriptionDraft(description);
            setTaskDraft(editableBoxPermissions(taskSettings));
        }
    }, [open, title, description, selection?.nodeId, taskSettings]);

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className={styles.dialogOverlay} />

                <Dialog.Content
                    className={`${styles.dialogContent} ${isPurpleNode ? styles.purpleNodeDialog : ""}`}
                    onKeyDown={event => {
                        if (event.key === "Escape") {
                            event.preventDefault();
                            event.stopPropagation();
                            onOpenChange(false);
                        }
                    }}
                >
                    <Dialog.Title className={styles.dialogTitle}>{t("settingsPage.company.noteWorkflows.dialog.title")}</Dialog.Title>

                    <Dialog.Description className={styles.dialogDescription}>{t("settingsPage.company.noteWorkflows.dialog.description")}</Dialog.Description>

                    <label className={styles.dialogFieldLabel}>
                        {t("settingsPage.company.noteWorkflows.dialog.titleLabel")}

                        <input
                            className={styles.dialogInput}
                            type="text"
                            value={titleDraft}
                            maxLength={MAX_TITLE_LENGTH}
                            placeholder={t("settingsPage.company.noteWorkflows.dialog.titlePlaceholder")}
                            onChange={event => setTitleDraft(event.target.value)}
                        />
                    </label>

                    <label className={styles.dialogFieldLabel}>
                        {t("settingsPage.company.noteWorkflows.dialog.descriptionLabel")}

                        <textarea
                            className={styles.dialogTextarea}
                            value={descriptionDraft}
                            maxLength={maxDescriptionLength}
                            placeholder={t("settingsPage.company.noteWorkflows.dialog.placeholder")}
                            onChange={event => setDescriptionDraft(event.target.value)}
                        />

                        <span className={styles.dialogCharCount}>
                            {descriptionDraft.length} / {maxDescriptionLength}
                        </span>
                    </label>

                    {children}
                    {isPurpleNode && (
                        <NodeTaskSettings
                            value={taskDraft}
                            onChange={setTaskDraft}
                            directory={directory}
                            loading={membersLoading}
                            error={membersError}
                            onRetry={onRetryMembers}
                        />
                    )}

                    <div className={styles.dialogActions}>
                        <button
                            type="button"
                            className={styles.dialogDeleteButton}
                            onClick={() => {
                                onDelete();

                                onOpenChange(false);
                            }}
                        >
                            {t("settingsPage.company.noteWorkflows.dialog.delete")}
                        </button>

                        <div className={styles.dialogPrimaryActions}>
                            <Dialog.Close asChild>
                                <button type="button" className={styles.dialogCancelButton}>
                                    {t("settingsPage.account.cancel")}
                                </button>
                            </Dialog.Close>

                            <button
                                type="button"
                                className={styles.dialogSaveButton}
                                onClick={() => {
                                    onSave(titleDraft, descriptionDraft, isPurpleNode ? serializeBoxPermissions(taskDraft) : undefined);

                                    onOpenChange(false);
                                }}
                            >
                                {t("settingsPage.account.save")}
                            </button>
                        </div>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
